/** Auth API — standalone fetch functions for /api/v1/auth/* endpoints.
 *  Does NOT use the Jerry request() wrapper to avoid circular 401 handling. */

const AUTH_PREFIX = "/api/v1/auth";
const DEFAULT_TIMEOUT_MS = 10_000;

export interface LoginResponse {
  success: boolean;
  data: { access_token: string; refresh_token: string };
}

export interface MeResponse {
  success: boolean;
  data: {
    id: string;
    email: string;
    name: string;
    tenant_id: string;
  };
}

/** `POST /auth/refresh` response — rotates the access+refresh pair. */
export interface RefreshResponse {
  success: boolean;
  data: { access_token: string; refresh_token: string };
}

async function authFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

  try {
    const res = await fetch(`${AUTH_PREFIX}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options?.headers as Record<string, string> | undefined),
      },
      signal: controller.signal,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error?.message || `Auth error: ${res.status}`);
    }

    return res.json();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function loginApi(email: string, password: string): Promise<LoginResponse> {
  return authFetch<LoginResponse>("/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function fetchMeApi(token: string): Promise<MeResponse> {
  return authFetch<MeResponse>("/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/** `POST /auth/refresh` — exchange a refresh token for a rotated access+refresh
 * pair. Uses the standalone `authFetch` (not the central client) so a failing
 * refresh never recurses into 401 handling. */
export function refreshTokenApi(refreshToken: string): Promise<RefreshResponse> {
  return authFetch<RefreshResponse>("/refresh", {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}
